/*
 * Este archivo es temporal, hasta validar que la obtención de datos es correcta; se eliminará si así se decide.
 * Explicación de cada bloque del dashboard de rendimientos (icono «i»): un resumen para cualquiera y un detalle técnico.
 * Texto plano y sin traducir a propósito: es documentación para desarrollo y para enseñar al cliente
 * cómo sale cada dato. Al darlo por bueno se puede borrar este fichero y los <info-button>.
 *
 * Referencias usadas en todos los textos:
 *   Servicio: app/Models/Services/DashboardPerformance/PerformanceDashboardService.php
 *   API:      GET /api/v2/project/{project}/dashboard-performance/activities?week_start=&week_end=   (tabla, gráficas y ficha)
 *             GET /api/v2/project/{project}/dashboard-performance/activities/{activity}/charts        (series de la ficha)
 *   Corte:    hoy (Carbon::today()); la semana es la semana del proyecto que contiene hoy.
 *   LB:       línea base = fechas scheduled_start_date / scheduled_end_date de la actividad.
 *   Plan:     última planificación = fechas current_start_date / current_end_date.
 *   Medición física (assignments): type = 'App\Models\AssignmentType\Resource', assignable_type = 'App\Models\Activity',
 *             total_units > 0, unidad (measure_units.code) distinta de '%', variant_type no 11 (type2) ni 14 (type5),
 *             y sin padre Resource (parent_assignment_id nulo o padre de otro tipo, p. ej. CertifierHead).
 *   Línea OK: assignment_lines.status = 0 y deleted_at nulo.
 *   Jornada:  projects.configs.man_hour (8 si no existe). En takeoff: HH por persona y semana / días laborables de la semana.
 *   Días laborables: projects.configs.work_days + festivos del calendario del proyecto.
 *   Rama:     la actividad padre (parent_id nulo) y todas sus subactividades; se quitan las subactividades con status = 6
 *             (canceladas) y las copias con el mismo planning_code dentro del mismo padre (se queda el id menor).
 *   Takeoff:  projects.configs.progress_type_mode = 'takeoff'. El avance de la actividad sale de sus mediciones TAG
 *             (variant_type 14 = type5 o 15 = type6) y las líneas de avance viven en las etapas (hijos del TAG).
 *             HH programadas = Σ etapa.total_quantity × etapa.weight / 100 × boq_roots.adjusted_man_hour_index del stage.
 *             HH por persona y semana = configs.man_hour_per_person_month / (52 / 12).
 *   n/c:      no calculable con los datos del proyecto; la razón sale en la nota amarilla bajo cada tabla.
 */

export const PERFORMANCE_INFO_TITLE = 'Cómo se calcula'

export const PERFORMANCE_INFO_SECTIONS = {
  summary: 'En pocas palabras',
  detail: 'Detalle técnico',
}

/*
 * Explicación general de cada bloque, para quien no conoce el sistema por dentro.
 * Mismas claves que PERFORMANCE_INFO; se muestra antes del detalle técnico.
 */
export const PERFORMANCE_INFO_SUMMARY = {
  summary: `Cuántas actividades se revisan esta semana y qué parte de la obra representan:
cuánto pesan en el coste de ejecución, cuántas están en la ruta crítica y cuántas tienen su partida desviada.
El motivo de cada una está en la tabla 3.`,

  selection: `No se revisan todas las actividades, solo las importantes de la semana.
Una actividad entra si se está trabajando esta semana, si se puede medir (metros, kilos, apoyos…)
y si cumple al menos una condición:
- está en la ruta crítica (si se retrasa, se retrasa la obra);
- pesa mucho en el total de la obra;
- su partida se ha desviado bastante de lo presupuestado.`,

  kpi_priority: `Cuántas actividades ha marcado el equipo como prioridad P1 y cuántas como P2.
La prioridad se elige en la tabla 2.
Rojo si hay alguna P1; amarillo si solo hay P2; verde si no hay ninguna.`,

  kpi_max_delay: `El mayor retraso que esperamos en el final de la obra, en días, y qué actividad lo provoca.
Se compara lo que falta por hacer con el ritmo medio que lleva cada actividad desde que empezó,
y se le descuenta la holgura (el margen que tiene antes de retrasar la obra).
Rojo si alguna actividad retrasa la obra; amarillo si no hay datos para calcularlo; verde si ninguna la retrasa.`,

  kpi_overdue_actions: `Cuántas acciones acordadas tienen la fecha ya pasada y siguen sin cerrarse.
Rojo si hay alguna; verde si no.`,

  chart_progress: `Para cada actividad, tres porcentajes de avance a día de hoy:
- Presupuestado: lo que debería llevar según el plan original (línea base).
- Planificado: lo que debería llevar según el último plan.
- Real: lo que realmente lleva.
Si el real está por debajo de los otros dos, la actividad va atrasada.`,

  chart_staff: `Cuántas personas, de media, se planificaron y cuántas han trabajado realmente en cada actividad desde que empezó.
No hay personal presupuestado en el sistema, por eso no aparece esa barra.`,

  chart_weeks: `Cuántas semanas harían falta para terminar lo que queda de cada actividad:
- naranja: si se sigue al ritmo medio que lleva desde que empezó;
- azul: si se trabaja al ritmo que marca el plan para las próximas 6 semanas.
La raya roja son las semanas que quedan hasta la fecha de fin. Si la barra la supera, no se llega a tiempo.`,

  attention: `La tabla de trabajo de la reunión: una fila por actividad revisada.
Primero las P1, luego las P2 y luego el resto; dentro de cada grupo, las que más retrasan la obra arriba.
Muestra si es crítica, cuánto lleva frente a lo presupuestado y cuántos días va tarde.
Las columnas con lápiz (prioridad, situación, causa, acción, responsable y fecha) se escriben aquí mismo.
Al pulsar una fila, la ficha de abajo pasa a mostrar esa actividad.`,

  reasons: `Por qué está cada actividad en la lista.
En verde la condición que cumple; con una basta.`,

  sheet: `La ficha de una actividad: se elige en la celda amarilla.
Arriba, su nombre, la unidad en que se mide, su partida del presupuesto, si es de ruta crítica y la prioridad que le ha puesto el equipo.
Debajo, la semana y el motivo por el que se sigue.`,

  sheet_actual_progress: `Cuánto lleva hecho la actividad, frente a lo que debería llevar según el plan original.`,

  sheet_project_delay: `Cuántos días se retrasaría el final de la obra por esta actividad si sigue al ritmo medio que lleva desde que empezó.
Debajo, los días que va tarde y la holgura que tiene antes de retrasar la obra.`,

  sheet_weeks_to_end: `Semanas que quedan hasta la fecha de fin prevista y cuánto falta por hacer.
Si sale en negativo, la fecha de fin ya pasó.`,

  sheet_actual_yield: `Horas de trabajo que hacen falta para producir una unidad (un apoyo, un metro…), desde el inicio.
Cuanto más bajo, más productivo. Se compara con lo presupuestado y lo planificado; en rojo si gasta más horas de las previstas.`,

  past: `Desde el inicio de la actividad hasta hoy: cuánto se ha avanzado, con cuánta gente y con qué productividad,
frente a lo presupuestado y lo planificado.`,

  future: `Qué hace falta para terminar en fecha:
- a qué ritmo hay que avanzar cada semana;
- cuántas personas harían falta;
- qué productividad hay que conseguir.
Se compara con lo que queda del presupuesto, con lo que dice el plan y con lo conseguido las últimas 4 semanas.
En rojo los ritmos que no llegan al necesario.`,

  sheet_review: `Lo que ha anotado el equipo en la tabla 2 para esta actividad:
dónde se pierde el avance, la causa y la acción acordada (con su responsable y fecha).`,

  weekly_progress: `Cuánto se avanzó en cada una de las últimas 6 semanas: lo presupuestado, lo planificado y lo real.`,

  weekly_staff: `Cuántas personas había previstas y cuántas han trabajado en cada una de las últimas 6 semanas.
Solo se dibujan las series que el sistema puede calcular.`,

  weekly_yield: `Horas por unidad en cada una de las últimas 6 semanas.
Solo se dibujan las series que el sistema puede calcular.`,

  history_progress: `El avance acumulado mes a mes desde que empezó la actividad.
La línea «Necesario» muestra cómo tendría que avanzar a partir de hoy para terminar en su fecha.`,

  history_staff: `Personas por mes: previstas, reales y, en los meses que quedan, las que harían falta para llegar a tiempo.`,

  history_yield: `Horas por unidad mes a mes: previstas, reales y, en los meses que quedan, las necesarias para llegar a tiempo.`,

  monthly: `Lo mismo que las gráficas mensuales, en números: cantidad hecha, avance acumulado, personas y productividad.
Hasta el mes actual; si la actividad dura más de un año, solo los últimos 12 meses.
En rojo lo que va peor de lo planificado.`,
}

export const PERFORMANCE_INFO = {
  summary: {
    title: 'Actividades analizadas',
    text: `"Se analizan N actividades · X % del coste de ejecución · N en ruta crítica · N con su partida desviada más de un 10 %"

- N actividades: las seleccionadas de la semana (ver "Selección de actividades").
- % del coste de ejecución: coste de las seleccionadas / coste de todas las actividades del proyecto × 100
  (summary.selected_cost / summary.total_cost).
- En ruta crítica: seleccionadas con activities.critical_path >= 1.
- Con su partida desviada: seleccionadas que entran por el criterio de desviación (> ±10 %).

Modo takeoff: no hay coste ni cantidad inicial, así que "% del coste" y "partida desviada" salen como n/c.
(Para el criterio de peso se usan las HH programadas; ver "Selección de actividades".)`,
  },

  selection: {
    title: 'Selección de actividades',
    text: `1) Candidatas: activities del proyecto con parent_id NULL cuyo rango actual solapa la semana:
   current_start_date <= week_end AND current_end_date >= week_start.

2) Medibles: candidatas cuya rama tiene al menos una medición física (ver cabecera del fichero).

3) Seleccionadas: medibles que cumplen al menos UNO de estos criterios:
   - Ruta crítica: activities.critical_path >= 1 (0 o NULL = no).
   - Peso: coste de la rama / coste total del proyecto × 100 >= 5 %.
   - Desviación de la partida: |coste − coste inicial| / coste inicial × 100 > 10 %.
   - Riesgo identificado: no hay registro de riesgos en el sistema; no se evalúa.

Coste (getExecutionCostByActivity), solo mediciones Resource sin hijos:
   coste         = Σ total_units × unit_price
   coste inicial = Σ COALESCE(total_units_initial, total_units) × unit_price
   No hay informe de control en el sistema: la desviación es esta aproximación.

Modo takeoff:
- Medibles: candidatas con al menos un TAG (medición Resource sin padre, variant_type 14 o 15).
- Peso: HH programadas de la rama / HH programadas del proyecto × 100 >= 5 %.
- Desviación: no hay coste ni cantidad inicial; no se evalúa.

Para probar:
- activities.critical_path = 1 en una actividad padre de la semana con medición física → entra por ruta crítica.
- unit_price en sus mediciones hasta que su coste sea >= 5 % del total → entra por peso.
- total_units_initial distinto de total_units (más de un 10 %) y unit_price > 0 → entra por desviación.
- Mover current_start_date / current_end_date fuera de la semana → deja de ser candidata.`,
  },

  kpi_priority: {
    title: 'Actividades en P1 / P2',
    text: `Valor "P1 / P2": seleccionadas con activity_performance_reviews.priority = 'p1' y = 'p2'.

Semáforo:
- ROJO · hay P1: al menos una P1.
- AMARILLO · hay P2: ninguna P1 y al menos una P2.
- VERDE: ninguna con prioridad.

La prioridad es manual: se elige en la columna Prior. de la tabla 2.`,
  },

  kpi_max_delay: {
    title: 'Retraso del proyecto al ritmo actual',
    text: `Valor: el mayor project_delay de las actividades seleccionadas y el código de esa actividad.

Por actividad (con su medición primaria; en takeoff, el avance de la actividad en puntos):
   ritmo actual          = ejecutado / semanas desde current_start_date hasta hoy
   semanas al ritmo      = pendiente / ritmo actual
   semanas que quedan    = (current_end_date − hoy) / 7
   días tarde            = max(0, redondeo((semanas al ritmo − semanas que quedan) × 7))
   holgura               = 0 si critical_path >= 1; si no, sin dato (P6 no trae la holgura total)
   retraso del proyecto  = max(0, días tarde − holgura); solo si existen los dos.

Semáforo:
- ROJO · N actividades retrasan el proyecto: N = seleccionadas con project_delay > 0.
- AMARILLO · sin dato de retraso: ninguna seleccionada tiene project_delay.
- VERDE: hay dato y ninguna retrasa el proyecto.

Para que una actividad tenga valor necesita: ruta crítica (para conocer la holgura), avance > 0,
current_start_date anterior a hoy y current_end_date.`,
  },

  kpi_overdue_actions: {
    title: 'Acciones acordadas vencidas',
    text: `Sale de activity_performance_reviews (columnas Acción acordada y Fecha de la tabla 2).

Vencida: actividad seleccionada con action no vacío, action_date < hoy y action_status distinto de 'completed'.
Semáforo: ROJO · N vencidas si hay alguna; VERDE si no.

Solo cuentan las actividades seleccionadas esta semana.
El estado de la acción no se edita en el dashboard: una acción deja de estar vencida al cambiar su fecha o al borrarla.

Para probar: guardar una acción con fecha de ayer → 1 vencida; poner una fecha futura → 0.`,
  },

  chart_progress: {
    title: 'Avance a origen',
    text: `Una barra por actividad seleccionada, en % a la fecha de corte, en el mismo orden que la tabla 2.

Presupuestado (LB) y Planificado (plan): PlannedExecutionService::getScheduledActivityProgress(actividad, hoy).
- Si la actividad tiene filas en planned_executions:
  % = Σ quantity con plan_date <= hoy / Σ quantity total × 100 (mismo valor para LB y plan).
- Si no: % lineal por duración entre {prefijo}_start_date y {prefijo}_end_date
  (prefijo 'scheduled' para LB, 'current' para el plan).

Real: activities.progress (el avance guardado de la actividad).

Para probar:
- Cambiar scheduled_end_date frente a current_end_date (sin planned_executions) → se separan las dos primeras barras.
- Cambiar activities.progress → cambia la barra Real.`,
  },

  chart_staff: {
    title: 'Personal a origen (personas medias)',
    text: `Planificado:
- Si la actividad padre tiene activities.people > 0: ese valor.
- Si no: Σ (people × días laborables de cada subactividad dentro de [current_start_date, min(current_end_date, hoy)])
  / días laborables de ese rango del padre.
- Sin dato si la actividad no ha empezado o nadie tiene people.

Real: con las líneas OK de la rama que tienen people > 0 y start_date <= hoy:
   Σ people × días laborables [start_date, min(end_date, hoy)] / días laborables [primera start_date, hoy].

Presupuestado: no hay personal presupuestado en el sistema; no se dibuja.

Modo takeoff (las líneas no traen people), sobre las semanas desde current_start_date hasta min(current_end_date, hoy):
- Planificado = HH programadas × % planificado al corte / 100 / (HH por persona y semana × semanas).
- Real = HH registradas / (HH por persona y semana × semanas).
  HH registradas: type6 → HH programadas de la etapa × progress% de la etapa; type5 → líneas OK × peso% × índice HH.
- Sin configs.man_hour_per_person_month no hay personal.

Para probar: activities.people = 5 → Planificado = 5; líneas OK con people y fechas → aparece Real.`,
  },

  chart_weeks: {
    title: 'Semanas para terminar (frente)',
    text: `Por actividad, con su medición primaria. Pendiente = total − ejecutado (assignments.progress).

- Al ritmo actual (naranja): ritmo medio desde el inicio = ejecutado / semanas desde current_start_date hasta hoy.
  Semanas = pendiente / ritmo. Sin barra si la actividad no ha empezado o no tiene avance.
- Con el plan a 6 semanas (azul): ritmo del plan = total × (% plan en min(hoy + 6 sem., current_end_date) − % plan hoy) / 100
  / semanas de ese tramo. Semanas = pendiente / ritmo del plan. Es 0 si current_end_date ya pasó.
- Raya roja: semanas que quedan = (current_end_date − hoy) / 7. Negativo = fin de programa ya pasado.

Medición primaria (misma regla que la planificación semanal):
- Un solo BOQ vinculado en activities_boq_roots → la medición con ese boq_root_id.
- Varios BOQ → la del vínculo con is_primary = 1.
- Si no → si la rama tiene una sola medición física (o varias del mismo BOQ), esa.
- Si no → no hay primaria y la actividad no tiene barras.
En modo subactividad, las mediciones de la rama con el mismo boq_root_id se suman en una sola.

Modo takeoff: una sola medición por actividad, "Avance de la actividad (todos los TAG)", en puntos de avance:
total 100, ejecutado = activities.progress, pendiente = 100 − ejecutado; mismas fórmulas de ritmo.

Para probar: registrar avance en la medición primaria → baja la barra naranja.`,
  },

  attention: {
    title: 'Actividades que requieren atención',
    text: `Una fila por actividad seleccionada. Orden: prioridad (P1, P2, sin prioridad) y, dentro de cada una,
retraso del proyecto de mayor a menor (las que no tienen dato, al final). Las gráficas del punto 1 usan el mismo orden.

- Crítica: Sí si critical_path >= 1; "Pasa a Sí" si no lo es pero los días tarde superan la holgura; si no, No.
- Avance real: activities.progress; en rojo si va por debajo del presupuestado.
  La "F" indica que no hay medición primaria con cantidad y el avance sale del campo progreso.
- Avance presup.: % de LB al corte (igual que la gráfica "Avance a origen").
- Días tarde (ritmo actual): ver "Retraso del proyecto al ritmo actual".
- Holgura: 0 si critical_path >= 1; n/c si no (P6 no trae la holgura total).
- Retraso del proyecto: días tarde − holgura; n/c si no hay holgura.
- Semanas de tajo liberado: n/c, no hay dato de tajo liberado en el sistema.
- Prior., Situación, Causa, Acción acordada, Responsable, Fecha: activity_performance_reviews.
  Se editan en la fila (requiere project.can_admin) y se guardan al salir del campo
  (la prioridad y la fecha, al cambiarlas) con PUT .../dashboard-performance/activities/{activity}/review.`,
  },

  reasons: {
    title: 'Por qué se analizan estas actividades',
    text: `Una fila por actividad seleccionada, en el mismo orden que la tabla 2.

- Ruta crítica: activities.critical_path >= 1 → "Sí" en verde.
- % del coste: coste de la rama / coste total del proyecto × 100. Verde si >= 5 %.
  En takeoff: n/c y, debajo, el % de las HH programadas (lo que usa el criterio de peso).
- Desviación: (coste − coste inicial) / coste inicial × 100. Verde si |valor| > 10 %.
  n/c si no hay coste inicial con precio, y siempre en takeoff.
- Motivo del seguimiento: frase con los criterios que cumple (reasons[] de la API).

Basta con un criterio en verde para que la actividad esté seleccionada.`,
  },

  sheet: {
    title: 'Ficha de evolución de actividad',
    text: `Celda amarilla: selector de actividad (las mismas de la tabla 2, en el mismo orden). Pulsar una fila de la tabla 2 también la elige.

Cabecera:
- Unidad: la de la medición elegida (en takeoff, "%" porque se mide en puntos de avance de la actividad).
- Partida: código del BOQ vinculado en activities_boq_roots (el único o el marcado is_primary); n/c si no tiene.
- RUTA CRÍTICA: si critical_path >= 1.
- Prioridad: activity_performance_reviews.priority (se elige en la tabla 2).
- Medición: solo aparece si la actividad tiene varias mediciones; por defecto la primaria.

Línea de debajo: semana del proyecto (fecha de corte) · proyecto · "Se sigue por": los motivos de la tabla 3.`,
  },

  sheet_actual_progress: {
    title: 'Avance real',
    text: `Valor: activities.progress de la actividad.
Debajo, "presupuestado": % de LB al corte (igual que en la gráfica "Avance a origen").`,
  },

  sheet_project_delay: {
    title: 'Retraso del proyecto (días)',
    text: `Con la medición elegida (en takeoff, el avance de la actividad en puntos):
   ritmo actual = ejecutado / semanas desde current_start_date hasta hoy
   días tarde   = max(0, redondeo((pendiente / ritmo actual − (current_end_date − hoy) / 7) × 7))
   holgura      = 0 si critical_path >= 1; si no, n/c
   retraso      = días tarde − holgura (en rojo si > 0; n/c si no hay holgura)

Sin ritmo (actividad sin empezar o sin avance) no hay días tarde.`,
  },

  sheet_weeks_to_end: {
    title: 'Semanas hasta fin de programa',
    text: `Valor: (current_end_date − hoy) / 7, con 1 decimal. En rojo si es negativo.
Debajo: fin = current_end_date; pendientes = total − ejecutado de la medición elegida.`,
  },

  sheet_actual_yield: {
    title: 'Rendimiento real a origen (h/ud)',
    text: `Real = HH registradas / cantidad ejecutada, con las líneas OK de la medición elegida que tienen
start_date, end_date <= hoy y people > 0. HH de una línea = people × días laborables [start_date, end_date] × jornada.

Debajo:
- Presupuestado: no hay horas por unidad del presupuesto → n/c.
- Planificado: man_hour_index de la medición si existe; si no (única medición o primaria),
  HH programadas / total, con HH programadas = activities.people × días laborables de la actividad × jornada.
- "+X % horas sobre lo presupuestado": (real − presupuestado) / presupuestado, solo si hay presupuestado.
- En rojo si el real supera al presupuestado (o al planificado si no hay presupuestado).

Modo takeoff (h por punto de avance de la actividad):
- Real = HH registradas de las etapas / avance de la actividad.
- Presupuestado = HH programadas de las etapas / 100 (las HH salen del presupuesto: índice HH ajustado).
- Planificado: n/c (la planificación no asigna personas a la actividad).`,
  },

  past: {
    title: 'A pasado (a origen)',
    text: `Avance (%): Presup. = % LB al corte; Planif. = % plan al corte; Real = activities.progress (en rojo si < planificado).

Personas (media):
- Presup.: n/c (no hay personal presupuestado).
- Planif. y Real: ver "Personal a origen".

Rendimiento (h/ud): ver "Rendimiento real a origen".
- Fuera de takeoff: Presup. n/c; Planif. y Real con horas de personas.
- Takeoff: Presup. desde el índice HH; Planif. n/c; Real = HH registradas / avance.`,
  },

  future: {
    title: 'A futuro (hasta fin de programa)',
    text: `Columnas: Presup. restante · Plan 6 semanas · Necesario para llegar · Real (últimas 4 sem.).

Avance (ud/semana):
- Presup. restante: lo que le falta a la LB, repartido hasta su fecha fin
  = total × (100 − % LB al corte) / 100 / semanas hasta scheduled_end_date. Sin dato si esa fecha ya pasó.
- Plan 6 semanas: ritmo del plan (ver "Semanas para terminar").
- Necesario: pendiente / semanas hasta current_end_date (solo si quedan semanas).
- Real (últimas 4 sem.): cantidad de las líneas OK de los últimos 28 días / 4,
  cada línea prorrateada por los días laborables que caen en la ventana.
- En rojo: Plan 6 semanas y Real por debajo del necesario.

Personas (media):
- Presup. restante: n/c (no hay personal presupuestado).
- Plan 6 semanas: personas planificadas, si a la actividad le quedan días laborables.
- Necesario: pendiente × h/ud real reciente (si no hay, h/ud real a origen) / (días laborables que quedan × jornada).
- Real (últimas 4 sem.): personas medias de las líneas OK con people de los últimos 28 días. n/c en takeoff.

Rendimiento (h/ud):
- Presup. restante: el h/ud presupuestado (en takeoff, desde el índice HH; fuera, n/c).
- Plan 6 semanas: el h/ud planificado (n/c en takeoff).
- Necesario: HH que quedan / pendiente; HH que quedan = personas planificadas × días laborables que quedan × jornada
  (si no hay personas: HH programadas − HH registradas).
- Real (últimas 4 sem.): HH / cantidad de las líneas OK con people de los últimos 28 días. n/c en takeoff.`,
  },

  sheet_review: {
    title: 'Situación, causa y acción',
    text: `Lo guardado en activity_performance_reviews para la actividad:
- Situación: situation.
- Causa (proyecto): cause.
- Acción acordada: action · action_manager · action_date.

Aquí solo se muestran; se escriben en la tabla 2.`,
  },

  weekly_progress: {
    title: 'Avance semanal · últimas 6 semanas',
    text: `Barras de las 6 semanas del proyecto que terminan en la semana de corte (S = número de semana del proyecto),
de la medición elegida, en unidades por semana (en takeoff, puntos de avance de la actividad).

- Presup.: lo que gana la LB en la semana = (% LB al fin de la semana − % LB el día antes de empezar) / 100 × total.
- Planif.: distribución semanal guardada de la medición (weekly_distributions); si no hay, lo mismo con el % del plan.
- Real: Σ cantidad de las líneas OK de la medición con end_date dentro de la semana (hasta hoy).
  En takeoff, las líneas de las etapas pasadas a puntos de la actividad (llevan la fecha de la importación).

Para probar: crear líneas OK con end_date en alguna de las últimas 6 semanas → sube la barra Real de esa semana.`,
  },

  weekly_staff: {
    title: 'Personas por semana · últimas 6 semanas',
    text: `- Planif.: personas planificadas a origen si la actividad tiene días laborables en la semana; 0 si no.
- Real: Σ people × días laborables de cada línea OK dentro de la semana / días laborables de la semana (hasta hoy),
  con las líneas OK con people > 0 de la rama.
- Presup.: no hay personal presupuestado; no se dibuja.

Modo takeoff (las líneas no traen personas):
- Presup. / Planif.: HH programadas × (% LB / % plan que gana la semana) / 100 / HH por persona y semana.
- Real: no se puede calcular; no se dibuja.`,
  },

  weekly_yield: {
    title: 'Rendimiento por semana · últimas 6 semanas',
    text: `Solo de la medición elegida.

- Planif.: personas planificadas × días laborables de la semana × jornada / cantidad planificada de la semana.
- Real: HH / cantidad de las líneas OK de la medición con end_date en la semana, con people > 0 y start_date.
- Presup.: no hay horas por unidad del presupuesto; no se dibuja.

Modo takeoff: solo Presup., constante = HH programadas de las etapas / 100 (h por punto de avance).
Planif. y Real no se pueden calcular; no se dibujan.`,
  },

  history_progress: {
    title: 'Avance acumulado por mes de obra',
    text: `Mes de obra 1 = mes de projects.current_start_date.
Meses desde el de current_start_date hasta el de current_end_date de la actividad.

- Presup. / Planif.: % de LB / plan acumulado al último día de cada mes.
- Real: Σ de las líneas OK fechadas hasta fin de mes (en el mes en curso, hasta hoy), en % de la actividad.
  Meses futuros y meses pasados sin líneas fechadas: vacío.
- Necesario: rampa lineal por días laborables desde el real de hoy hasta 100 % en current_end_date:
     real hoy + (100 − real hoy) × días laborables [hoy, fin de mes] / días laborables [hoy, current_end_date].

Para probar: mover current_end_date más lejos → la rampa del Necesario se suaviza.`,
  },

  history_staff: {
    title: 'Personas por mes de obra',
    text: `- Planif.: personas planificadas a origen en los meses con días laborables de la actividad.
- Real: Σ people × días laborables de cada línea OK dentro del mes / días laborables del mes (hasta hoy en el mes en curso).
- Necesarias: solo meses futuros; valor constante de "Personas · Necesario para llegar" de la tabla A futuro.
- Presup.: no hay personal presupuestado; no se dibuja.

Modo takeoff: Presup. y Planif. = HH programadas que gana el mes (LB / plan) / (HH por persona y semana × semanas del mes).
Real no se puede calcular; no se dibuja.`,
  },

  history_yield: {
    title: 'Rendimiento por mes de obra',
    text: `Solo de la medición elegida.

- Planif.: personas planificadas × días laborables del mes × jornada / cantidad planificada del mes.
- Real: HH / cantidad de las líneas OK de la medición con end_date en el mes y people > 0.
- Necesario: solo meses futuros; valor constante de "Rendimiento · Necesario para llegar" de la tabla A futuro.
- Presup.: no hay horas por unidad del presupuesto; no se dibuja.

Modo takeoff: Presup. constante (HH programadas de las etapas / 100) y Necesario; Planif. y Real no se dibujan.`,
  },

  monthly: {
    title: 'Evolución mensual',
    text: `Meses de obra desde el inicio de la actividad hasta el del corte (los futuros no tienen real); si son más de 12, los últimos 12.
Medición elegida en la ficha (en takeoff, el avance de la actividad en puntos).

- Avance (ud): Presup. / Planif. = (% LB / % plan a fin de mes − el del mes anterior) / 100 × total;
  Real = Σ cantidad de las líneas OK con end_date en el mes.
- Avance acum. (%): Presup. y Real, igual que la gráfica "Avance acumulado".
- Personas y Rendimiento: igual que las gráficas mensuales (n/c donde la gráfica no dibuja la serie).
- Pérdidas (% horas perdidas, estimadas): n/c; el sistema no registra horas no productivas.

En rojo: avance real por debajo del planificado y h/ud real por encima del planificado (o del presupuestado si no hay planificado).`,
  },
}
